import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, Search, AlertCircle, ArrowLeft } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { 
    getSwappableTokens, 
    isMultiChainToken, 
    getTokenChainIds, 
    getChainDisplayName, 
    getChainAbbreviation,
    getTokenStandard,
    getChainFeeDescription,
    CHAIN_IDS 
} from '@/lib/tokenRegistry';
import { toast } from 'sonner';
import type { CustomTokenPair } from '../backend';
import { useGetCustomTokenPairs } from '../hooks/useQueries';

interface Token {
    symbol: string;
    name: string;
    chainId?: number; // Optional chain ID for multi-chain tokens
}

interface TokenSelectorProps {
    tokens: Token[];
    selectedToken: Token | null;
    onSelectToken: (token: Token) => void;
    showBalance?: boolean;
    balances?: Record<string, number>;
    fromTokenSymbol?: string; // If provided, check swappability
    currentChainId?: number;
    // REMOVED: userTokenPairs prop - now fetched directly via hook to avoid bundler conflicts
}

// CRITICAL: Define empty array constant OUTSIDE component to prevent bundler hoisting issues
const EMPTY_CUSTOM_TOKEN_PAIRS: CustomTokenPair[] = [];

export default function TokenSelector({
    tokens, 
    selectedToken, 
    onSelectToken,
    showBalance = false,
    balances = {},
    fromTokenSymbol,
    currentChainId
}: TokenSelectorProps) {
    // CRITICAL: Fetch customTokenPairs directly via hook to avoid bundler prop hoisting issues
    const { data: customTokenPairsData } = useGetCustomTokenPairs();
    const safeCustomTokenPairs: CustomTokenPair[] = Array.isArray(customTokenPairsData) ? customTokenPairsData : EMPTY_CUSTOM_TOKEN_PAIRS;
    
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showChainSelection, setShowChainSelection] = useState(false);
    const [selectedMultiChainToken, setSelectedMultiChainToken] = useState<Token | null>(null);
    
    // Get swappable tokens if fromToken is provided
    // CRITICAL: Use useMemo to prevent bundler from creating bare references
    const swappableSymbols = useMemo(() => {
        if (!fromTokenSymbol) return null;
        // Ensure all values are explicitly captured in closure
        const tokenSymbol = fromTokenSymbol;
        const chain = currentChainId;
        const tokenPairs = safeCustomTokenPairs;
        return getSwappableTokens(tokenSymbol, chain, tokenPairs);
    }, [fromTokenSymbol, currentChainId, safeCustomTokenPairs]);
    
    // Check if a token is custom
    const isCustomToken = (symbol: string): boolean => {
        return safeCustomTokenPairs.some(t => t.symbol.toUpperCase() === symbol.toUpperCase());
    };

    const filteredTokens = tokens.filter(
        (token) =>
            token.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
            token.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSelectToken = (token: Token) => {
        console.log('[TokenSelector] handleSelectToken called with:', token);
        
        // Check if this is a multi-chain token
        if (isMultiChainToken(token.symbol)) {
            // Show chain selection submenu instead of selecting immediately
            setSelectedMultiChainToken(token);
            setShowChainSelection(true);
            return;
        }
        
        // For single-chain tokens, proceed with normal selection
        handleTokenSelection(token);
    };

    const handleTokenSelection = (token: Token) => {
        // Check if token is swappable (only when selecting "to" token with a "from" token already selected)
        const isSwappable = !fromTokenSymbol || !swappableSymbols || 
            swappableSymbols.includes(token.symbol.toUpperCase());
        
        if (!isSwappable) {
            // Show error toast
            toast.error('Swap Not Available', {
                description: `Cannot swap ${fromTokenSymbol} to ${token.symbol}. This pair is not supported by available bridges or liquidity providers.`,
                duration: 5000,
            });
            // Still close the dialog even if token is not swappable
            setOpen(false);
            setSearchQuery('');
            setShowChainSelection(false);
            setSelectedMultiChainToken(null);
            return;
        }
        
        // Call onSelectToken immediately, then close dialog
        // This ensures state updates happen before dialog closes
        console.log('[TokenSelector] Calling onSelectToken with:', token);
        try {
            onSelectToken(token);
            console.log('[TokenSelector] onSelectToken called successfully');
        } catch (error) {
            console.error('[TokenSelector] Error updating token:', error);
            toast.error('Failed to select token');
        }
        
        // Close dialog after state update
        setOpen(false);
        setSearchQuery('');
        setShowChainSelection(false);
        setSelectedMultiChainToken(null);
    };

    const handleSelectChain = (chainId: number) => {
        if (!selectedMultiChainToken) return;
        
        // Create token with chain ID
        const tokenWithChain: Token = {
            ...selectedMultiChainToken,
            chainId,
        };
        
        handleTokenSelection(tokenWithChain);
    };

    return (
        <Dialog 
            open={open} 
            onOpenChange={(newOpen) => {
                console.log('[TokenSelector] Dialog onOpenChange:', newOpen);
                setOpen(newOpen);
                if (!newOpen) {
                    // Clear search when dialog closes
                    setSearchQuery('');
                    setShowChainSelection(false);
                    setSelectedMultiChainToken(null);
                }
            }}
        >
            <DialogTrigger asChild>
                <Button variant="outline" className="h-14 px-4 gap-2 min-w-[140px]">
                    {selectedToken ? (
                        <div className="flex items-center gap-2">
                            <span className="font-semibold">{selectedToken.symbol}</span>
                            {selectedToken.chainId && (
                                <Badge variant="secondary" className="text-xs">
                                    {getChainAbbreviation(selectedToken.chainId)}
                                </Badge>
                            )}
                        </div>
                    ) : (
                        <span>Select Token</span>
                    )}
                    <ChevronDown className="h-4 w-4 ml-auto" />
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>
                        {showChainSelection && selectedMultiChainToken ? (
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6"
                                    onClick={() => {
                                        setShowChainSelection(false);
                                        setSelectedMultiChainToken(null);
                                    }}
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                </Button>
                                Select {selectedMultiChainToken.symbol} Network
                            </div>
                        ) : (
                            'Select a Token'
                        )}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search by name or symbol"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    {fromTokenSymbol && swappableSymbols && (
                        <div className="text-xs text-muted-foreground flex items-center gap-1 px-1">
                            <AlertCircle className="h-3 w-3" />
                            <span>Grayed out tokens are not available for swap from {fromTokenSymbol}</span>
                        </div>
                    )}
                    <ScrollArea className="h-[400px] pr-4">
                        <div className="space-y-1">
                            {showChainSelection && selectedMultiChainToken ? (
                                // Show chain selection menu
                                (() => {
                                    const chainIds = getTokenChainIds(selectedMultiChainToken.symbol);
                                    return chainIds.map((chainId) => {
                                        const chainName = getChainDisplayName(chainId);
                                        const chainAbbr = getChainAbbreviation(chainId);
                                        const standard = getTokenStandard(chainId);
                                        const feeDesc = getChainFeeDescription(chainId);
                                        
                                        return (
                                            <button
                                                key={chainId}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleSelectChain(chainId);
                                                }}
                                                className="w-full flex items-center gap-3 p-3 rounded-lg transition-colors hover:bg-accent cursor-pointer"
                                                type="button"
                                            >
                                                <div className="flex-1 text-left">
                                                    <div className="font-semibold flex items-center gap-2">
                                                        {selectedMultiChainToken.symbol} ({chainName})
                                                        <Badge variant="outline" className="text-xs">
                                                            {chainAbbr}
                                                        </Badge>
                                                    </div>
                                                    <div className="text-sm text-muted-foreground">
                                                        {standard} • {feeDesc}
                                                    </div>
                                                </div>
                                                {selectedToken?.symbol === selectedMultiChainToken.symbol && 
                                                 selectedToken?.chainId === chainId && (
                                                    <div className="h-2 w-2 rounded-full bg-primary" />
                                                )}
                                            </button>
                                        );
                                    });
                                })()
                            ) : filteredTokens.length > 0 ? (
                                filteredTokens.map((token) => {
                                    const balance = balances[token.symbol] || 0;
                                    // Debug logging for ICP tokens
                                    if (import.meta.env.DEV && (token.symbol === 'ICP' || token.symbol === 'ckBTC' || token.symbol === 'ckETH' || token.symbol === 'ckDOGE')) {
                                        console.log(`[TokenSelector] ${token.symbol}: balance=${balance}, balances object:`, balances);
                                    }
                                    const isSwappable = !fromTokenSymbol || !swappableSymbols || 
                                        swappableSymbols.includes(token.symbol.toUpperCase());
                                    
                                    return (
                                        <button
                                            key={token.symbol}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                console.log('[TokenSelector] Button clicked for token:', token.symbol);
                                                handleSelectToken(token);
                                            }}
                                            disabled={!isSwappable && !!fromTokenSymbol}
                                            className={cn(
                                                "w-full flex items-center gap-3 p-3 rounded-lg transition-colors",
                                                isSwappable || !fromTokenSymbol
                                                    ? "hover:bg-accent cursor-pointer"
                                                    : "opacity-50 cursor-not-allowed bg-muted/30"
                                            )}
                                            type="button"
                                        >
                                                <div className="flex-1 text-left">
                                                    <div className="font-semibold flex items-center gap-2">
                                                        {token.symbol}
                                                        {isMultiChainToken(token.symbol) && (
                                                            <Badge variant="secondary" className="text-xs">
                                                                Multi-chain
                                                            </Badge>
                                                        )}
                                                        {isCustomToken(token.symbol) && (
                                                            <Badge variant="outline" className="text-xs">Custom</Badge>
                                                        )}
                                                        {!isSwappable && fromTokenSymbol && (
                                                            <span className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                                                                <AlertCircle className="h-3 w-3" />
                                                                Not available
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-muted-foreground">{token.name}</div>
                                                </div>
                                            <div className="text-right">
                                                {showBalance && (
                                                    <div className="text-sm font-medium">
                                                        {balance.toFixed(4)}
                                                    </div>
                                                )}
                                                {selectedToken?.symbol === token.symbol && 
                                                 (!selectedToken.chainId || !isMultiChainToken(token.symbol)) && (
                                                    <div className="h-2 w-2 rounded-full bg-primary mt-1 ml-auto" />
                                                )}
                                            </div>
                                        </button>
                                    );
                                })
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    No tokens found
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                </div>
            </DialogContent>
        </Dialog>
    );
}
